import { useCallback, useEffect, useMemo, useState } from 'react';

type DefaultOpen = boolean | ((id: string) => boolean);

const resolveDefaultOpen = (defaultOpen: DefaultOpen, id: string): boolean =>
  typeof defaultOpen === 'function' ? defaultOpen(id) : defaultOpen;

export const useAccordionOpenStates = ({
  storageKey,
  accordionIds,
  defaultOpen,
  onToggle,
}: {
  storageKey: string | null;
  accordionIds: string[];
  defaultOpen: DefaultOpen;
  onToggle?: (id: string, isOpen: boolean) => void;
}) => {
  const initialAccordionStates = useMemo(() => {
    if (!storageKey || accordionIds.length === 0) {
      return {};
    }

    const savedStates = sessionStorage.getItem(storageKey);
    if (savedStates) {
      try {
        return JSON.parse(savedStates);
      } catch (e) {
        console.error(
          'Failed to parse accordion states from sessionStorage',
          e,
        );
      }
    }

    const defaultStates: Record<string, boolean> = {};
    accordionIds.forEach(id => {
      defaultStates[id] = resolveDefaultOpen(defaultOpen, id);
    });
    return defaultStates;
  }, [accordionIds, defaultOpen, storageKey]);

  const [accordionOpenStates, setAccordionOpenStates] = useState<
    Record<string, boolean>
  >(initialAccordionStates);
  const [seededStorageKey, setSeededStorageKey] = useState(storageKey);

  // Re-seed during render on key change (e.g. kit switch) so the effects
  // below never see the old key's states paired with the new key.
  if (seededStorageKey !== storageKey) {
    setSeededStorageKey(storageKey);
    setAccordionOpenStates(initialAccordionStates);
  }

  useEffect(() => {
    if (Object.keys(initialAccordionStates).length === 0) return;

    setAccordionOpenStates(prevStates => {
      if (Object.keys(prevStates).length > 0) return prevStates;
      return initialAccordionStates;
    });
  }, [initialAccordionStates]);

  useEffect(() => {
    if (storageKey && Object.keys(accordionOpenStates).length > 0) {
      sessionStorage.setItem(storageKey, JSON.stringify(accordionOpenStates));
    }
  }, [accordionOpenStates, storageKey]);

  const handleAccordionToggle = useCallback(
    (id: string, isOpen: boolean) => {
      onToggle?.(id, isOpen);
      setAccordionOpenStates(prevStates => ({
        ...prevStates,
        [id]: isOpen,
      }));
    },
    [onToggle],
  );

  return {
    accordionOpenStates,
    handleAccordionToggle,
  };
};
